export type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CropBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

const MIN_CROP_DIM = 32;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function initialCropArea(bounds: CropBounds, aspectRatio?: number): CropArea {
  const boundsWidth = bounds.right - bounds.left;
  const boundsHeight = bounds.bottom - bounds.top;
  let width = boundsWidth * 0.8;
  let height = boundsHeight * 0.8;
  if (aspectRatio) {
    width = Math.min(boundsWidth * 0.9, boundsHeight * 0.9 * aspectRatio);
    height = width / aspectRatio;
  }
  return {
    x: bounds.left + (boundsWidth - width) / 2,
    y: bounds.top + (boundsHeight - height) / 2,
    width,
    height,
  };
}

export function calculateLockedCropArea(
  current: CropArea,
  direction: string,
  delta: { x: number; y: number },
  bounds: CropBounds,
  aspectRatio: number,
): CropArea {
  if (direction === 'move') {
    return {
      ...current,
      x: clamp(current.x + delta.x, bounds.left, bounds.right - current.width),
      y: clamp(current.y + delta.y, bounds.top, bounds.bottom - current.height),
    };
  }

  const west = direction.includes('w');
  const north = direction.includes('n');
  const anchorX = west ? current.x + current.width : current.x;
  const anchorY = north ? current.y + current.height : current.y;

  const widthFromX = current.width + (west ? -delta.x : delta.x);
  const widthFromY = (current.height + (north ? -delta.y : delta.y)) * aspectRatio;
  const proposed =
    Math.abs(widthFromX - current.width) >= Math.abs(widthFromY - current.width)
      ? widthFromX
      : widthFromY;

  const roomX = west ? anchorX - bounds.left : bounds.right - anchorX;
  const roomY = north ? anchorY - bounds.top : bounds.bottom - anchorY;
  const maxWidth = Math.min(roomX, roomY * aspectRatio);
  const minWidth = Math.min(Math.max(MIN_CROP_DIM, MIN_CROP_DIM * aspectRatio), maxWidth);
  const width = clamp(proposed, minWidth, maxWidth);
  const height = width / aspectRatio;

  return {
    x: west ? anchorX - width : anchorX,
    y: north ? anchorY - height : anchorY,
    width,
    height,
  };
}
